import { useState } from 'react';
import { ChemicalCard } from './ChemicalCard';
import { ChemicalTable } from './ChemicalTable';
import { ViewMode } from '@/pages/Directory';
import { Card } from '@/components/ui/card';

export type ChemicalSortField = 'chemical_name' | 'supplier' | 'chemical_type' | 'cas_number';
export type SortDirection = 'asc' | 'desc';

interface Chemical {
  id: string;
  chemical_name: string;
  cas_number: string | null;
  chemical_type: string;
  supplier: string | null;
  molecular_formula: string | null;
  molecular_weight: number | null;
  hazard_level?: 'low' | 'moderate' | 'high' | 'unknown';
  created_at: string;
}

interface ChemicalResultsProps {
  chemicals: Chemical[];
  viewMode: Exclude<ViewMode, 'benchmark'>;
  isLoading: boolean;
}

export const ChemicalResults = ({ 
  chemicals, 
  viewMode, 
  isLoading 
}: ChemicalResultsProps) => {
  const [sortField, setSortField] = useState<ChemicalSortField>('chemical_name');
  const [sortDirection, setSortDirection] = useState<SortDirection>('asc');

  const handleSort = (field: ChemicalSortField) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('asc');
    }
  };

  const sortedChemicals = [...chemicals].sort((a, b) => {
    let aValue = '';
    let bValue = '';

    switch (sortField) {
      case 'chemical_name':
        aValue = a.chemical_name || '';
        bValue = b.chemical_name || '';
        break;
      case 'supplier':
        aValue = a.supplier || '';
        bValue = b.supplier || '';
        break;
      case 'chemical_type':
        aValue = a.chemical_type || '';
        bValue = b.chemical_type || '';
        break;
      case 'cas_number':
        aValue = a.cas_number || '';
        bValue = b.cas_number || '';
        break;
    }

    const comparison = aValue.localeCompare(bValue);
    return sortDirection === 'asc' ? comparison : -comparison;
  });

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64">
        <p>Loading raw materials...</p>
      </div>
    );
  }

  if (chemicals.length === 0) {
    return (
      <Card className="p-8 text-center">
        <p className="text-muted-foreground">
          No raw materials found matching your criteria.
        </p>
      </Card>
    );
  }

  if (viewMode === 'list') {
    return (
      <ChemicalTable 
        chemicals={sortedChemicals}
        sortField={sortField}
        sortDirection={sortDirection}
        onSort={handleSort}
      />
    );
  }

  return (
    <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-6">
      {sortedChemicals.map((chemical) => (
        <ChemicalCard key={chemical.id} chemical={chemical} />
      ))}
    </div>
  );
};